<template>
    <div class="section-wrapper">
        <div class="generic-destination-container" :class="{'mobyle-styles': getMobileAppState}">
            <!-- Account International Number Input  -->
            <input-generic
            id="accountInternationalNumber"
            :params="destinationIbanParams"
            :inputValue="formData.accountInternationalNumber"
            @input="[
                emit('updateFormData', { field: 'accountInternationalNumber', inputValue: $event }),
                clearFieldError('accountInternationalNumber')
            ]"/>
            <!-- Validate Generic Destination -->
            <button-tertiary
            class="validate-button"
            :label="t('transfers.instantBAI.step1.validate')"
            :icon="'check-line'"
            :disabled="!formData.accountInternationalNumber || destinationIbanParams.errorState"
            :style="{'margin-top': marginValidator && !getMobileAppState ? '24px' : '0'}"
            @clicked="validateDestinationHandler"/>
        </div>
        <div v-if="validatedIban" class="destination-wrapper">
            <!-- Beneficiary Name Output -->
            <text-blocks-generic
            v-if="applyOwnCustomerInfo"
            :params="beneficiaryNameParams"/>
            <!-- Beneficiary name input -->
            <input-generic
            v-if="!applyOwnCustomerInfo"
            id="name"
            :params="beneficiaryNameInputParams"
            :inputValue="formData.name"
            @onBlur="beneficiaryNameValidation"
            @input="[emit('updateFormData', { field: 'name', inputValue: $event }), clearFieldError('name')]" />
            <!-- Nif Output -->
            <text-blocks-generic
            v-if="applyOwnCustomerInfo"
            :params="nifParams"/>
            <!-- Nif input -->
            <input-generic
            v-if="!applyOwnCustomerInfo"
            id="fiscalDocumentNumber"
            :params="nifInputParams"
            :inputValue="formData.fiscalDocumentNumber"
            @input="[emit('updateFormData', { field: 'fiscalDocumentNumber', inputValue: $event }), clearFieldError('fiscalDocumentNumber')]"
            @onBlur="nifInputValidator"/>
            <!-- Nationality Dropdown -->
            <dropdown-generic
            id="nationality"
            :params="nationalityDropDownParams"
            :options="parsedCountriesNationalityList"
            :selectedOption="formData.nationality"
            @clicked="[
                emit('updateFormData', { field: 'nationality', inputValue: $event }),
                clearFieldError('nationality')
            ]"/>
            <divider-generic v-if="(validatedIban || !transferByIban) && !applyOwnCustomerInfo" :applyBorder="false" :dividerSize="'small'" :icon="'house'" :title="t('transfers.international.genericLabels.beneficiaryAddressDivider')"/>
            <!-- Country Input -->
            <dropdown-generic
            id="internationalTransferCountryId"
            :params="countriesDropdownParams"
            :options="parsedCountriesList"
            :selectedOption="formData.internationalTransferCountryId"
            @clicked="[
                emit('updateFormData', { field: 'internationalTransferCountryId', inputValue: $event }),
                clearFieldError('internationalTransferCountryId')
            ]"/>
            <!-- Postal Code input -->
            <input-generic
            :params="postalCodeInputParams"
            :inputValue="formData.zipCode"
            @input="emit('updateFormData', { field: 'zipCode', inputValue: $event })"/>
            <!-- City input -->
            <input-generic
            id="city"
            :params="cityInputParams"
            :inputValue="formData.city"
            @input="[emit('updateFormData', { field: 'city', inputValue: $event }), clearFieldError('city')]"/>
            <!-- Address input -->
            <input-generic
            id="address"
            :params="addressInputParams"
            :inputValue="formData.address"
            @input="[emit('updateFormData', { field: 'address', inputValue: $event }), clearFieldError('address')]"/>
            <!-- Door input -->
            <input-generic
            id="door"
            :params="doorInputParams"
            :inputValue="formData.door"
            @input="[emit('updateFormData', { field: 'door', inputValue: $event }), clearFieldError('door')]"/>
            <!-- Floor input -->
            <input-generic
            :params="floorInputParams"
            :inputValue="formData.floor"
            @input="emit('updateFormData', { field: 'floor', inputValue: $event })"/>
            <!-- PhoneNumber input -->
            <input-generic
            id="phoneNumber"
            :params="phoneNumberInputParams"
            :inputValue="formData.phoneNumber"
            @input="[emit('updateFormData', { field: 'phoneNumber', inputValue: $event }), phoneNumberInputParams.errorState = false]"
            @onBlur="phoneNumberInputValidator"/>
            <!-- Email input -->
            <input-generic
            id="email"
            :params="emailInputParams"
            :inputValue="formData.email"
            @input="[emit('updateFormData', { field: 'email', inputValue: $event.trim() }), emailInputParams.errorState = false]"
            @onBlur="emailValidation"/>
        </div>
    </div>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'
import InputGeneric from '@/components/generic/inputs/Generic'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'

/* Vue && Assets */
import { ref, computed, watch } from 'vue'
import { noSpecialCharTransfers, removeAccents, onlyNumbersChars, noSpecialChar, onlyNumbersNoSpace } from '@/assets/scripts/validators'
import { emailValidator, internationalPhoneNumberValidator } from '@/assets/scripts/validators'
import { PHONE_NUMBER_INTERNATIONAL } from '@/assets/constants/masks'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getCountriesList, getCountriesNationalityList } = mapGetters(moduleMapper.TRANSFERS_INSTANT_BAI)
const { getMobileAppState } = mapGetters()

/** Props
@formData {Object} - Object with form data
@tabsList {Array} - Tab List
@validatedIban  {Boolean} - Boolean controller show validated iban inputs
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@clearForm {Boolean} - Boolean controller to clear form and errors
@applyOwnCustomerInfo {Boolean} Boolean controller to show Own Customer Info
@fieldsWithError {Array} - Input List with error MDW provides to use
@invalidIbanError {Boolean} Boolean controller to apply iban error
@invalidIbanErrorMessage {String} Mdw Iban Error Message
*/
const props = defineProps({
    formData: Object,
    validatedIban: Boolean,
    showInputsErrors: Boolean,
    clearForm: Boolean,
    applyOwnCustomerInfo: Boolean,
    fieldsWithError: Array,
    invalidIbanError: Boolean,
    invalidIbanErrorMessage: String
})

const emit = defineEmits([
    'updateFormData',
    'validateGenericDestination',
    'validateSwiftCode',
    'resetShowInputsErrors',
    'formIsCleared',
    'scrollTo'
])

const marginValidator = computed(() => {
    return !destinationIbanParams.value.errorState
})

/*************************************************
*                                                *
*              Generic Destination               *
*                                                *
*************************************************/

const destinationIbanParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.destinationIban'),
    placeholder: t('transfers.instantBAI.step1.placeholders.destinationIban'),
    mask: {
        regex: 'PT50 0008 [0-9]{4} [0-9]{4} [0-9]{4} [0-9]{4} [0-9]{1}',
        placeholder: '',
        showMaskOnHover: false,
        unmaskFn: (value) => {
            return value.replace(/\s+/g, '').toUpperCase()
        }
    },
    mandatory: true,
    errorState: false,
    errorMessage: null
})

const validateDestinationHandler = () => {
    if (!destinationIbanParams.value.errorState) {
        clearFormErrors()
        emit('formIsCleared')
        emit('validateIban')
    }
}

/*************************************************
*                                                *
*                Beneficiary Name                *
*                                                *
*************************************************/

const beneficiaryNameParams = computed(() => {
    return {
        label: t('transfers.instantBAI.genericLabels.beneficaryName'),
        content: props.formData.name || '-'
    }
})

const beneficiaryNameInputParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.beneficaryName'),
    placeholder: t('transfers.instantBAI.step1.placeholders.beneficaryName'),
    maxLength: 80,
    ourMask: {
        validatorFn: value => { return noSpecialCharTransfers(value) },
        parseInputText: value => { return removeAccents(value) }
    },
	mandatory: true,
    errorState: false,
    errorMessage: null,
    bottomMessage: t('transfers.instantBAI.step1.beneficiaryNameNote')
})

const beneficiaryNameValidation = () => {
    if (props.formData.name.length && props.formData.name.length < 5) {
        beneficiaryNameInputParams.value.errorState = true
        beneficiaryNameInputParams.value.errorMessage = t('transfers.instantBAI.step1.beneficiaryNameValidation')

        return
    }

    /* [ourString].trim().indexOf(' ') === -1 checks if we have 2 or more words separated by a empty space */
    if (!!props.formData.name.length && props.formData.name.trim().indexOf(' ') === -1) {
        beneficiaryNameInputParams.value.errorState = true
        beneficiaryNameInputParams.value.errorMessage = t('transfers.instantBAI.step1.beneficiaryNameErrorMessage')
    }
}
/*************************************************
*                                                *
*                       NIF                      *
*                                                *
*************************************************/

const nifParams = computed(() => {
    return {
        label: t('transfers.instantBAI.genericLabels.nif'),
        content: props.formData.fiscalDocumentNumber || '-'
    }
})

const noSpecialCharAndSpaces = string => {
	const regex = /^[a-zA-Z0-9]*$/
	return regex.test(string)
}

const nifInputParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.nif'),
    placeholder: t('transfers.instantBAI.step1.placeholders.nif'),
    maxLength: 20,
    ourMask: {
        validatorFn: value => { return noSpecialCharAndSpaces(value) },
        parseInputText: value => { return removeAccents(value).toUpperCase() }
    },
	mandatory: true,
    errorState: false,
    errorMessage: null
})

const nifInputValidator = () => {
    if (props.formData.fiscalDocumentNumber.length) {
        nifInputParams.value.errorState = props.formData.fiscalDocumentNumber.toString().length < 5
        if (nifInputParams.value.errorState) {
            nifInputParams.value.errorMessage = t('transfers.instantBAI.step1.nifErrorMessage', { minValue: 5, maxValue: 20 })

            emit('scrollTo', getElementOffsetTopPosition('fiscalDocumentNumber'))
        }
    }
}

/*************************************************
*                                                *
*                  PhoneNumber                   *
*                                                *
*************************************************/

const phoneNumberInputParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.phoneNumber'),
    placeholder: t('transfers.instantBAI.step1.placeholders.phoneNumber'),
    maxLength: 24,
    mask: PHONE_NUMBER_INTERNATIONAL,
    errorState: false,
    errorMessage: null,
    bottomMessage: t('transfers.international.step1.phoneNumberWarning')
})

const phoneNumberInputValidator = () => {
    if (!!props.formData.phoneNumber && !internationalPhoneNumberValidator(props.formData.phoneNumber)) {
        phoneNumberInputParams.value.errorState = true
        phoneNumberInputParams.value.errorMessage = t('phoneLengthTMXError')

        emit('scrollTo', getElementOffsetTopPosition('phoneNumber'))
    }
}

/*************************************************
*                                                *
*                      Email                     *
*                                                *
*************************************************/

const emailInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.email'),
    placeholder: t('transfers.instantBAI.step1.placeholders.email'),
    maxLength: 42,
	errorState: false,
    errorMessage: null
})

const emailValidation = () => {
    if (!!props.formData.email && !emailValidator(props.formData.email)) {
        emailInputParams.value.errorState = true
        emailInputParams.value.errorMessage = t('invalidEmailError')

        emit('scrollTo', getElementOffsetTopPosition('email'))
    }
}

/*************************************************
*                                                *
*                    Country                     *
*                                                *
*************************************************/
const nationalityDropDownParams = ref({
    labelText: t('transfers.international.genericLabels.nationality'),
    placeholder: t('transfers.international.step1.placeholders.nationality'),
	mandatory: true,
	errorState: false,
    errorMessage: null,
    withSearch: true
})

const countriesDropdownParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.country'),
    placeholder: t('transfers.instantBAI.step1.placeholders.country'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const parsedCountriesList = computed(() => {
    return getCountriesList.value.map(country => ({
        ...country,
        id: country.id,
        text: country.description
    }))
})

const parsedCountriesNationalityList = computed(() => {
    return getCountriesNationalityList.value.map(country => ({
        ...country,
        id: country.id,
        text: country.description
    }))
})
/*************************************************
*                                                *
*                Postal Code                     *
*                                                *
*************************************************/

const postalCodeInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.postalCode'),
    placeholder: t('transfers.instantBAI.step1.placeholders.postalCode'),
	errorState: false,
    errorMessage: null,
    maxLength: 12,
    ourMask: {
        validatorFn: (value) => { return onlyNumbersChars(value) },
        parseInputText: (value) => { return removeAccents(value) }
    },
    applyNumbersIntInputMode: true
})

/*************************************************
*                                                *
*                     City                       *
*                                                *
*************************************************/

const cityInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.city'),
    placeholder: t('transfers.instantBAI.step1.placeholders.city'),
	mandatory: true,
	errorState: false,
    errorMessage: null,
    maxLength: 30,
    ourMask: {
        validatorFn: (value) => { return noSpecialChar(value) },
        parseInputText: (value) => { return removeAccents(value) }
    }
})

/*************************************************
*                                                *
*                   Address                      *
*                                                *
*************************************************/

const addressInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.address'),
    placeholder: t('transfers.instantBAI.step1.placeholders.address'),
	mandatory: true,
	errorState: false,
    errorMessage: null,
    ourMask: {
        validatorFn: value => { return noSpecialChar(value) },
        parseInputText: value => { return removeAccents(value) }
    }
})

/*************************************************
*                                                *
*                   Door Number                  *
*                                                *
*************************************************/

const doorInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.doorNumber'),
    placeholder: t('transfers.instantBAI.step1.placeholders.doorNumber'),
	mandatory: true,
	errorState: false,
    errorMessage: null,
    maxLength: 12,
    ourMask: {
        validatorFn: (value) => { return onlyNumbersNoSpace(value) },
        parseInputText: (value) => { return removeAccents(value) }
    },
    applyNumbersIntInputMode: true
})

/*************************************************
*                                                *
*                    Floor                       *
*                                                *
*************************************************/

const floorInputParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.floor'),
    placeholder: t('transfers.instantBAI.step1.placeholders.floor'),
	errorState: false,
    errorMessage: null,
    maxLength: 48,
    ourMask: {
        validatorFn: (value) => { return onlyNumbersNoSpace(value) },
        parseInputText: (value) => { return removeAccents(value) }
    },
    applyNumbersIntInputMode: true
})

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/
const destinationMandatoryList = ref([
    'accountInternationalNumber',
    'name',
    'fiscalDocumentNumber',
    'nationality',
    'internationalTransferCountryId',
    'city',
    'address',
    'door'
])

const fieldsWithErrorMessage = ref({
    accountInternationalNumber: destinationIbanParams.value,
    name: beneficiaryNameInputParams.value,
    phoneNumber: phoneNumberInputParams.value,
    fiscalDocumentNumber: nifInputParams.value,
    nationality: nationalityDropDownParams.value,
    internationalTransferCountryId: countriesDropdownParams.value,
    city: cityInputParams.value,
    address: addressInputParams.value,
    door: doorInputParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	let mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, destinationMandatoryList.value)

    if (props.applyOwnCustomerInfo) {
        mandatoryFieldsEmptyList = mandatoryFieldsEmptyList.filter(string => string !== 'name' && string !== 'fiscalDocumentNumber')
    }

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))
    }

    if (props.fieldsWithError.length) {
        props.fieldsWithError.forEach(field => {
            if (fieldsWithErrorMessage.value[field.parsedFieldOnError]) {
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorState = true
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorMessage = field.errorMessage
            }
        })

        emit('scrollTo', getElementOffsetTopPosition(props.fieldsWithError[0]))
    }
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

watch(() => props.invalidIbanError, newVal => {
    if (newVal) {
        destinationIbanParams.value.errorState = true
        destinationIbanParams.value.errorMessage = props.invalidIbanErrorMessage
    }
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/

const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
	clearFormErrors()
	emit('formIsCleared')
})
</script>

<style lang="scss" scoped>
.section-wrapper {
    width: 100%;

    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;

    .generic-destination-container {
        width: 100%;
        min-height: 88px;

        display: flex;
        justify-content: center;
        align-items: center;
        gap: 24px;

        &.mobyle-styles {
            flex-direction: column;

            .validate-button {
                align-self: center;
                width: 100%;
                max-width: unset
            }
        }

        .validate-button {
            width: 100%;
            max-width: 176px;
        }
    }

    .destination-wrapper {
        width: 100%;

        display: flex;
        justify-content: center;
        flex-direction: column;
        gap: 24px;

        margin-top: 24px;
    }
}
</style>
